/**
 * Rock TCS — frontend mirror of the pure calculation helpers.
 *
 * MUST stay in sync with backend/src/calculation/rockTcsCalculations.ts and
 * backend/src/calculation/rockTcsConformity.ts — the backend is the source of
 * truth on save; this mirror exists only for live preview while typing.
 * (Same convention as atterberg/computations.ts — see LESSONS-LEARNED
 * 2026-04-27 "Frontend mirror of pure-function calc module".)
 */

export const FLATNESS_TOLERANCE_MM = 0.025;
export const PERPENDICULARITY_TOLERANCE_DEG = 0.25;
export const PARALLELISM_TOLERANCE_DEG = 0.13;
export const SIDE_STRAIGHTNESS_TOLERANCE_MM = 0.5;
export const LD_RATIO_MIN = 2.0;
export const LD_RATIO_MAX = 2.5;
export const ULTRASONIC_TOLERANCE_DEFAULT_MM = 0.35;
/** Fixed per-row reference distance (workbook $H$40 = 150 mm — the UK1401
 *  Surfer's fixed transducer base). Mirrors backend constants/rockTcs.ts. */
export const ULTRASONIC_REFERENCE_DISTANCE_MM = 150;

const avg = (values: (number | null)[]): number | null => {
  const nums = values.filter((v): v is number => v !== null && Number.isFinite(v));
  return nums.length ? nums.reduce((s, v) => s + v, 0) / nums.length : null;
};

// ── Specimen properties ──────────────────────────────────────────────────────

export interface SpecimenPreview {
  avgDiameterMm: number | null;
  avgLengthMm: number | null;
  lOverD: number | null;
  lOverDPass: boolean | null;
  areaMm2: number | null;
  volumeCm3: number | null;
  bulkDensityGCm3: number | null;
  avgGaugeLengthMm: number | null;
}

export function computeSpecimenPreview(input: {
  diameters: (number | null)[];
  lengths: (number | null)[];
  massG: number | null;
  gaugeLengths: (number | null)[];
}): SpecimenPreview {
  const avgDiameterMm = avg(input.diameters);
  const avgLengthMm = avg(input.lengths);
  const lOverD = avgDiameterMm && avgLengthMm ? avgLengthMm / avgDiameterMm : null;
  const areaMm2 = avgDiameterMm !== null ? Math.PI * (avgDiameterMm / 2) ** 2 : null;
  const volumeCm3 = areaMm2 !== null && avgLengthMm !== null ? (areaMm2 * avgLengthMm) / 1000 : null;
  return {
    avgDiameterMm,
    avgLengthMm,
    lOverD,
    lOverDPass: lOverD === null ? null : lOverD > LD_RATIO_MIN && lOverD < LD_RATIO_MAX,
    areaMm2,
    volumeCm3,
    bulkDensityGCm3: input.massG !== null && volumeCm3 ? input.massG / volumeCm3 : null,
    avgGaugeLengthMm: avg(input.gaugeLengths),
  };
}

// ── Conformity (flatness / parallelism / perpendicularity) ──────────────────

interface FitResult {
  slope: number;
  intercept: number;
  angleDeg: number;
}

function linearFit(points: { x: number; y: number }[]): FitResult | null {
  const n = points.length;
  if (n < 2) return null;
  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return null;
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept, angleDeg: Math.atan(slope) * (180 / Math.PI) };
}

export interface ConformitySeriesPreview {
  endNumber: number;
  diameterLine: number;
  angleDeg: number | null;
  maxDeviationMm: number | null;
  /** points for the inline chart: measured + best-fit ± tolerance band */
  band: { positionMm: number; fitMm: number; upperMm: number; lowerMm: number }[];
}

export function computeConformitySeriesPreview(
  endNumber: number,
  diameterLine: number,
  points: { positionMm: number; valueMm: number | null }[],
): ConformitySeriesPreview {
  const pts = points.filter((p) => p.valueMm !== null).map((p) => ({ x: p.positionMm, y: p.valueMm as number }));
  const fit = pts.length >= 2 ? linearFit(pts) : null;
  if (!fit) return { endNumber, diameterLine, angleDeg: null, maxDeviationMm: null, band: [] };
  const maxDeviationMm = Math.max(...pts.map((p) => Math.abs(p.y - (fit.slope * p.x + fit.intercept))));
  const band = points.map((p) => {
    const fitMm = fit.slope * p.positionMm + fit.intercept;
    return { positionMm: p.positionMm, fitMm, upperMm: fitMm + FLATNESS_TOLERANCE_MM, lowerMm: fitMm - FLATNESS_TOLERANCE_MM };
  });
  return { endNumber, diameterLine, angleDeg: fit.angleDeg, maxDeviationMm, band };
}

export interface ConformityPreview {
  series: ConformitySeriesPreview[];
  flatnessPass: boolean | null;
  perpendicularityPass: boolean | null;
  parallelismPass: boolean | null;
  sideStraightnessPass: boolean | null;
  lOverDPass: boolean | null;
  failReasons: string[];
  /** Numeric metrics matching the workbook's "Conformity Results" table. */
  flatnessMaxDevMm: number | null; // Excel C24 (max |reading − best-fit|)
  perpendicularityDeg: number | null; // Excel C22 (max |best-fit angle|)
  parallelismDeg: number | null; // Excel C26 (max end1↔end2 angle diff)
}

export function computeConformityPreview(input: {
  series: { endNumber: number; diameterLine: number; points: { positionMm: number; valueMm: number | null }[] }[];
  sideStraightnessMm: number | null;
  lOverD: number | null;
  lOverDPass: boolean | null;
}): ConformityPreview {
  const series = input.series.map((s) => computeConformitySeriesPreview(s.endNumber, s.diameterLine, s.points));
  const withFit = series.filter((s) => s.maxDeviationMm !== null);
  const flatnessMaxDevMm = withFit.length === 0 ? null : Math.max(...withFit.map((s) => s.maxDeviationMm as number));
  const flatnessPass = flatnessMaxDevMm === null ? null : flatnessMaxDevMm <= FLATNESS_TOLERANCE_MM;
  const angles = withFit.filter((s) => s.angleDeg !== null);
  const perpendicularityDeg = angles.length === 0 ? null : Math.max(...angles.map((s) => Math.abs(s.angleDeg as number)));
  const perpendicularityPass = perpendicularityDeg === null ? null : perpendicularityDeg <= PERPENDICULARITY_TOLERANCE_DEG;
  let parallelismPass: boolean | null = null;
  const diffs: number[] = [];
  for (const line of [1, 2]) {
    const e1 = series.find((s) => s.endNumber === 1 && s.diameterLine === line)?.angleDeg;
    const e2 = series.find((s) => s.endNumber === 2 && s.diameterLine === line)?.angleDeg;
    if (e1 != null && e2 != null) diffs.push(Math.abs(e1 - e2));
  }
  const parallelismDeg = diffs.length > 0 ? Math.max(...diffs) : null;
  if (parallelismDeg !== null) parallelismPass = parallelismDeg <= PARALLELISM_TOLERANCE_DEG;
  const sideStraightnessPass = input.sideStraightnessMm === null ? null : input.sideStraightnessMm <= SIDE_STRAIGHTNESS_TOLERANCE_MM;

  const failReasons: string[] = [];
  if (flatnessPass === false) failReasons.push('end flatness exceeds 0.025 mm');
  if (perpendicularityPass === false) failReasons.push('end perpendicularity exceeds 0.25°');
  if (parallelismPass === false) failReasons.push('end parallelism exceeds 0.13°');
  if (sideStraightnessPass === false) failReasons.push(`side straightness = ${input.sideStraightnessMm} mm`);
  if (input.lOverDPass === false && input.lOverD !== null) {
    failReasons.push(input.lOverD < LD_RATIO_MIN
      ? `L/D ratio < 2.0 [${input.lOverD.toFixed(2)}]`
      : `L/D ratio > 2.5 [${input.lOverD.toFixed(2)}]`);
  }
  return {
    series, flatnessPass, perpendicularityPass, parallelismPass, sideStraightnessPass,
    lOverDPass: input.lOverDPass, failReasons,
    flatnessMaxDevMm, perpendicularityDeg, parallelismDeg,
  };
}

export function buildShapeToleranceStatement(failReasons: string[], extraReason?: string | null): string | null {
  const reasons = [...failReasons];
  if (extraReason && extraReason.trim() !== '') reasons.push(extraReason.trim());
  return reasons.length === 0 ? null : `Specimen does not meet ASTM D4543 shape tolerance (${reasons.join(', ')}).`;
}

// ── Ultrasonic checker ───────────────────────────────────────────────────────

export function computeUltrasonicPreview(
  readings: { velocityMS: number | null; timeUS: number | null }[],
  toleranceMm: number | null,
): {
  avgVelocityMS: number | null;
  avgTimeUS: number | null;
  rows: { distanceMm: number | null; status: 'Good' | 'Check' | null }[];
} {
  const tol = toleranceMm ?? ULTRASONIC_TOLERANCE_DEFAULT_MM;
  const valid = readings.filter((r) => r.velocityMS !== null && r.timeUS !== null);
  const avgVelocityMS = valid.length ? valid.reduce((s, r) => s + (r.velocityMS as number), 0) / valid.length : null;
  const avgTimeUS = valid.length ? valid.reduce((s, r) => s + (r.timeUS as number), 0) / valid.length : null;
  // Per-row check against the FIXED 150 mm reference (workbook $H$40) — rows
  // are independent of one another (BUG-2026-0167). Mirrors computeUltrasonic
  // in backend rockTcsConformity.ts.
  const distances = readings.map((r) => (r.velocityMS !== null && r.timeUS !== null ? (r.velocityMS * r.timeUS) / 1000 : null));
  return {
    avgVelocityMS,
    avgTimeUS,
    rows: distances.map((d) => ({
      distanceMm: d,
      status: d === null ? null : Math.abs(d - ULTRASONIC_REFERENCE_DISTANCE_MM) > tol ? 'Check' : 'Good',
    })),
  };
}

// ── Failure description (mirror of rockTcsConformity.buildFailureDescription) ─

function planePhrase(plane: string | null): string {
  if (!plane) return 'along the failure plane';
  const prep = plane === 'rock matrix' ? 'through ' : 'along ';
  const article = plane === 'intrusion' ? 'an ' : ['vein', 'microdefect', 'foliation'].includes(plane) ? 'a ' : '';
  return `${prep}${article}${plane}`;
}

const fmtAngle = (a: number | null): string => (a === null ? '—°' : `${Number.isInteger(a) ? a : a.toFixed(1)}°`);

/** Matching plane descriptions are said once, and ", respectively" is dropped
 *  with them (BUG-2026-0137 §7 + rework — backend mirror). */
function dualPlanes(p1: string | null, p2: string | null): { phrase: string; matched: boolean } {
  const first = planePhrase(p1);
  const second = planePhrase(p2);
  return first === second
    ? { phrase: first, matched: true }
    : { phrase: `${first} and ${second}`, matched: false };
}

export function buildFailureDescriptionPreview(input: {
  mode: string | null;
  primaryPlaneType: string | null;
  primaryPlaneAngleDeg: number | null;
  secondaryPlaneType?: string | null;
  secondaryPlaneAngleDeg?: number | null;
}): string | null {
  const { mode, primaryPlaneType: p1, primaryPlaneAngleDeg: a1 } = input;
  const p2 = input.secondaryPlaneType ?? null;
  const a2 = input.secondaryPlaneAngleDeg ?? null;
  if (!mode) return null;
  switch (mode) {
    case 'Shear':
      return `Specimen sheared ${planePhrase(p1)} at ${fmtAngle(a1)} to the load axis.`;
    case 'Axial Cleavage':
      return a1 === null
        ? `Specimen split ${planePhrase(p1)} subparallel to the load axis.`
        : `Specimen split ${planePhrase(p1)} at ${fmtAngle(a1)} to the load axis.`;
    case 'Fragmented': {
      const plural = p1 && ['vein', 'microdefect', 'intrusion', 'foliation'].includes(p1) ? 's' : '';
      return `Specimen fragmented ${planePhrase(p1)}${plural} at various angles to the load axis.`;
    }
    case 'Multiple Shear': {
      const dual = dualPlanes(p1, p2);
      return `Specimen sheared ${dual.phrase} at ${fmtAngle(a1)} and ${fmtAngle(a2)} to the load axis${dual.matched ? '' : ', respectively'}.`;
    }
    case 'Shear & Axial Cleavage':
      return `Specimen sheared ${planePhrase(p1)} at ${fmtAngle(a1)} to the load axis and split ${planePhrase(p2)} subparallel to the load axis.`;
    case 'Multiple Shear & Axial Cleavage': {
      const dual = dualPlanes(p1, p2);
      return dual.matched
        ? `Specimen sheared ${dual.phrase} at ${fmtAngle(a1)} and ${fmtAngle(a2)} to the load axis and split subparallel to the load axis.`
        : `Specimen sheared ${dual.phrase} at ${fmtAngle(a1)} and ${fmtAngle(a2)} to the load axis, respectively, and split subparallel to the load axis.`;
    }
    default:
      return null;
  }
}

// ── Poisson validity + comments assembly (REQ-09 mirror) ────────────────────

export type PoissonValidity = 'valid' | 'atypical' | 'invalid';

export function classifyPoisson(value: number | null): PoissonValidity | null {
  if (value === null) return null;
  if (value < 0 || value > 0.5) return 'invalid';
  if (value < 0.1 || value > 0.4) return 'atypical';
  return 'valid';
}

export function buildPoissonStatements(input: {
  poissonSecant: number | null;
  poissonTangent: number | null;
  altPercentOfTCS?: number | null;
  altPoissonSecant?: number | null;
  altPoissonTangent?: number | null;
}): string[] {
  const statements: string[] = [];
  const entries: [string, number | null][] = [
    ['secant', input.poissonSecant],
    ['tangent', input.poissonTangent],
  ];
  for (const [kind, value] of entries) {
    const validity = classifyPoisson(value);
    if (validity === 'atypical') {
      statements.push(`${kind.charAt(0).toUpperCase()}${kind.slice(1)} Poisson's ratio is outside the typical range for rock.`);
    } else if (validity === 'invalid') {
      statements.push(`Invalid ${kind} Poisson's ratio (${(value as number).toFixed(3)}).`);
    }
  }
  if (input.altPercentOfTCS != null) {
    // "(secant)" / "(tangent)" method suffix (BUG-2026-0137 §6 — backend mirror).
    // State BOTH values at the alternative % when both are available (BUG-2026-0166).
    const altEntries: [string, number | null | undefined][] = [
      ['secant', input.altPoissonSecant],
      ['tangent', input.altPoissonTangent],
    ];
    const alts = altEntries.filter(
      ([, v]) => v != null && (classifyPoisson(v) === 'valid' || classifyPoisson(v) === 'atypical'),
    );
    if (alts.length > 0) {
      const quoted = alts.map(([kind, v]) => `${(v as number).toFixed(3)} (${kind})`).join(' and ');
      statements.push(`Poisson's ratio is ${quoted} at ${input.altPercentOfTCS}% of TCS.`);
    }
  }
  return statements;
}

export function assembleReportComments(input: {
  shapeToleranceStatement: string | null;
  failureDescription: string | null;
  poissonStatements: string[];
  remarks: string | null;
}): string {
  const parts: string[] = [];
  if (input.shapeToleranceStatement) parts.push(input.shapeToleranceStatement);
  if (input.failureDescription) parts.push(input.failureDescription);
  parts.push(...input.poissonStatements);
  if (input.remarks && input.remarks.trim() !== '') parts.push(input.remarks.trim());
  return parts.join('\n');
}
